import Linkify from 'components/Linkify'
import { WithAccountProps } from 'hoc/withAccount'
import { AppointmentNote } from 'hooks/useAppointments/useAppointmentNotes'
import useAppointments from 'hooks/useAppointments/useAppointments'
import useActions from 'hooks/useTimeline/useActions'
import { useMemo } from 'react'
import { Modal, ModalProps } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import { BookingEventSource } from 'utils/amplitude.types'
import { generateAppointmentLink } from 'utils/appointments.utils'
import { isInsurancePatient } from 'utils/program.utils'
import { formatName } from 'utils/provider.utils'
import { formatDate } from 'utils/time.utils'
import '../styles.scss'
import { useSourceContext } from 'context/SourceContext/SourceProvider'
import { useFlags } from 'launchdarkly-react-client-sdk'
import useAppointmentTypeData from 'hooks/useAppointments/useAppointmentTypeData'
import AppointmentNoteActions from './AppointmentNoteActions'
import { useLabScheduling } from 'hooks/useLabScheduling/useLabscheduling'

interface AppointmentNoteModalProps extends ModalProps {
  note: AppointmentNote | null
  openFrom: BookingEventSource
  labsOrdered?: boolean
}

// At this time we can refeer to all the appointment types with this keys
const rdIdentifier = 'rd'
const mdIdentifier = 'md'
const nutritionIdentifier = 'nutrition'
const diagnosticIdentifier = 'diagnostic'
export type AppointmentBookingFlowType =
  | typeof rdIdentifier
  | typeof mdIdentifier
  | typeof nutritionIdentifier
  | typeof diagnosticIdentifier
  | null

const getAppointmentBookingFlow = (note: AppointmentNote | null) => {
  if (!note) {
    return null
  }

  const noteFollowupModule = note.appointment.type.followupModule

  if (noteFollowupModule.includes(mdIdentifier)) {
    return mdIdentifier
  } else if (noteFollowupModule.includes(rdIdentifier)) {
    return rdIdentifier
  } else if (noteFollowupModule.includes(nutritionIdentifier)) {
    return nutritionIdentifier
  } else if (noteFollowupModule.includes(diagnosticIdentifier)) {
    return diagnosticIdentifier
  } else {
    return null
  }
}

const AppointmentNoteModal: React.FC<
  WithAccountProps & AppointmentNoteModalProps
> = ({ user, note, openFrom, onHide, labsOrdered }) => {
  const history = useHistory()
  const { activeActions } = useActions()
  const { nonCancelledAppointments } = useAppointments()

  const { labFastFollowsCta } = useFlags()
  const { labAppointments } = useLabScheduling()
  const { source } = useSourceContext()

  const appointmentBookingFlow: AppointmentBookingFlowType = useMemo(() => {
    return getAppointmentBookingFlow(note)
  }, [note])

  const isTheLastAppointmentOfThisBookingTypeFlow = useMemo(() => {
    if (!appointmentBookingFlow || !note?.appointment?.sourceId) {
      return false
    }

    // Since apptList is ordered descending by date, it's ok to get first that matches the booking flow
    const firstAppointmentOfThisBookingTypeFlow = nonCancelledAppointments.find(
      (appt) => appt.type.followupModule.includes(appointmentBookingFlow)
    )

    return (
      firstAppointmentOfThisBookingTypeFlow &&
      firstAppointmentOfThisBookingTypeFlow.sourceId ===
        note.appointment.sourceId
    )
  }, [
    nonCancelledAppointments,
    appointmentBookingFlow,
    note?.appointment?.sourceId,
  ])

  const {
    followupAppointmentTypeId,
    appointmentPlanningHorizon,
  } = useAppointmentTypeData(
    note,
    appointmentBookingFlow,
    activeActions,
    source
  )

  const shouldShowCta = useMemo(() => {
    if (!note?.followupDate) {
      return false
    }

    const followupDate = new Date(note.followupDate)
    const todayPlusAppointmentPlanningHorizon = new Date()
    todayPlusAppointmentPlanningHorizon.setDate(
      todayPlusAppointmentPlanningHorizon.getDate() + appointmentPlanningHorizon
    )
    const isBeforeAppointmentPlanningHorizon =
      followupDate < todayPlusAppointmentPlanningHorizon

    return (
      isInsurancePatient(user) && // At this time we want to show this only for insurance patients
      note.appointment.type.category === 'Initial MD' && // At this time we want to show this only for initial MD appointments
      isBeforeAppointmentPlanningHorizon &&
      !!isTheLastAppointmentOfThisBookingTypeFlow &&
      !!followupAppointmentTypeId // This is the appointment type id from the pending action related to the booking flow
    )
  }, [
    user,
    isTheLastAppointmentOfThisBookingTypeFlow,
    followupAppointmentTypeId,
    appointmentPlanningHorizon,
  ])

  if (!note) {
    return <></>
  }

  const getFollowupDate = () => {
    if (!note.followupDate) {
      return undefined
    }

    const rawDate = new Date(note.followupDate)
    // set time to 00:00:00
    rawDate.setUTCHours(0, 0, 0, 0)
    return rawDate.toISOString()
  }

  return (
    <Modal
      onHide={onHide}
      show={note !== null}
      size="lg"
      centered
      className="appointment-note-modal"
    >
      <Modal.Header className="fs-6" closeButton>
        Note from Appointment on {formatDate(new Date(note.appointment.time))}{' '}
        with {formatName(note.appointment.provider)}
      </Modal.Header>

      <Modal.Body className="py-3 justify-content-center p-4">
        <AppointmentNoteActions
          shouldShowCta={shouldShowCta}
          labFastFollowsCta={labFastFollowsCta}
          hasUpcomingLabAppointment={
            labAppointments != null && labAppointments.length > 0
          }
          labsOrdered={!!labsOrdered}
          handleLegacyBookNextVisit={() =>
            history.push(
              generateAppointmentLink(
                followupAppointmentTypeId ?? '',
                {
                  source: openFrom,
                  patient_action_id: null,
                  patient_action_definition: null,
                  resource_id: null,
                },
                getFollowupDate()
              )
            )
          }
        />
        <Linkify>
          <pre className="ff-inter-medium">
            <p className="note-text">{note.text}</p>
          </pre>
        </Linkify>
        <div className="mt-4">
          Attachments:
          {note.attachments?.length ? (
            note.attachments.map((attachment) => (
              <li key={attachment.id}>
                <a href={attachment.fileUrl} target="_blank" rel="noreferrer">
                  {attachment.fileName}
                </a>
              </li>
            ))
          ) : (
            <p>None</p>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default AppointmentNoteModal
